import React from 'react';
import styled from 'styled-components';
import { FaDownload, FaRobot, FaComments, FaUserPlus, FaLanguage, FaUsers } from 'react-icons/fa';
import { createCustomAgentScreen1, createAgentScreen } from '../assets';

const HowItWorksSection = styled.section`
  padding: 6rem 0;
  background-color: var(--white);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, var(--accent-color), transparent);
    opacity: 0.2;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--text-dark);
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-medium);
  line-height: 1.6;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 600px;
`;

const Step = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const StepIconContainer = styled.div`
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(var(--primary-rgb), 0.15);
  
  svg {
    font-size: 1.75rem;
    color: var(--white);
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-dark);
`;

const StepDescription = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.6;
`;

const ImageContainer = styled.div`
  width: 100%;
  max-width: 380px;
  margin-left: auto;
  position: relative;
  
  @media (max-width: 992px) {
    order: -1;
    max-width: 340px;
    margin: 0 auto;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 20px;
    right: -20px;
    bottom: -20px;
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
    border-radius: var(--border-radius);
    opacity: 0.1;
    z-index: -1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  border: 5px solid white;
`;

const HowItWorks = () => {
  const steps = [
    {
      id: 1,
      icon: <FaDownload />,
      title: "Download Hallow",
      description: "Get our lightweight AI assistant from the Play Store. The app is optimized to work on all Android devices.",
      bgColor: "#8A2BE2",
      bgGradient: "#9370DB"
    },
    {
      id: 2,
      icon: <FaLanguage />,
      title: "Choose Your Language",
      description: "Select your preferred Indian language. Our AI understands and responds fluently in major regional languages.",
      bgColor: "#7B68EE",
      bgGradient: "#6A5ACD"
    },
    {
      id: 3,
      icon: <FaUsers />,
      title: "Select Your Agent",
      description: "Choose from specialized AI agents designed for farmers, rural businesses, students, and more. Each agent has domain expertise.",
      bgColor: "#483D8B",
      bgGradient: "#4B0082"
    },
    {
      id: 4,
      icon: <FaComments />,
      title: "Start Chatting",
      description: "Begin conversations with your AI assistant in your language. Works in both online and offline modes for your convenience.",
      bgColor: "#8A2BE2",
      bgGradient: "#4B0082"
    },
  ];

  return (
    <HowItWorksSection id="how-it-works">
      <Container>
        <SectionHeader>
          <SectionTitle>
            How It <span>Works</span>
          </SectionTitle>
          <SectionSubtitle>
            Getting started with Hallow is simple. Follow these steps to access 
            AI assistance in your preferred language and mode.
          </SectionSubtitle>
        </SectionHeader>
        
        <ContentWrapper>
          <StepsContainer>
            {steps.map(step => (
              <Step key={step.id}>
                <StepIconContainer 
                  bgColor={step.bgColor} 
                  bgGradient={step.bgGradient}
                >
                  {step.icon}
                </StepIconContainer>
                <StepContent>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
              </Step>
            ))}
          </StepsContainer>
          
          <ImageContainer>
            <Image src={createAgentScreen} alt="Choose your AI assistant" />
          </ImageContainer>
        </ContentWrapper>
      </Container>
    </HowItWorksSection>
  );
};

export default HowItWorks; 